import {
  toWSDateString,
  WSCard,
  WSDataItem,
  WSGrid,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayrollRun } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { PAYROLL_CURRENCY } from "../../constants";
import { selectorPayrollRunBreakdownAmounts } from "../../selectors/selectorPayrollRunBreakdownAmounts";
import { formatPayPeriodRange } from "../../utils";

const EMPTY_VALUE = "—";

type Props = {
  /** Must be read with `expand=breakdown`, which supplies the rollups below. */
  payrollRun: PayrollRun;
};

/**
 * What an employee payroll run costs the payer: gross pay plus payer taxes and
 * benefits, against the run's dates.
 *
 * Employee runs only. A contractor run funds payables net of deductions and is
 * summarised by the four tiles on `PayrollRunDetailsContractor`, the way the v1
 * Payables screen did it.
 */
export const PayrollRunDetailsCard: React.FC<Props> = ({ payrollRun }) => {
  const amounts = selectorPayrollRunBreakdownAmounts(payrollRun);

  const liabilityBreakdown = [
    { label: "Gross amount", amount: amounts.grossAmount },
    { label: "Payer taxes", amount: amounts.payerTaxes },
    { label: "Benefits", amount: amounts.benefits }
  ];

  return (
    <WSCard header={{ label: "Funding amount" }}>
      <WSGrid gutter="L">
        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSList gap="S">
            <WSInfoBox>
              <WSText.Paragraph color="gray600" mb="XS" weight="medium">
                Liability amount
              </WSText.Paragraph>
              <WSText.Heading1 color="gray500" weight="book">
                {amounts.liabilityAmount === undefined
                  ? "Not available"
                  : formatMoney(amounts.liabilityAmount, PAYROLL_CURRENCY)}
              </WSText.Heading1>
            </WSInfoBox>

            <WSInfoBox>
              <WSList gap="S">
                {liabilityBreakdown.map(item => (
                  <WSDataItem
                    key={item.label}
                    inline
                    label={item.label}
                    value={formatMoney(item.amount, PAYROLL_CURRENCY)}
                  />
                ))}
              </WSList>
            </WSInfoBox>
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSList gap="XL">
            <WSDataItem
              label="Payroll start date"
              value={
                payrollRun.payPeriodStartDate
                  ? toWSDateString(
                      payrollRun.payPeriodStartDate,
                      "monthDayYear"
                    )
                  : EMPTY_VALUE
              }
            />
            <WSDataItem
              label="Payroll finalization date"
              value={
                payrollRun.events?.finalizedAt
                  ? toWSDateString(
                      payrollRun.events.finalizedAt,
                      "monthDayYear"
                    )
                  : EMPTY_VALUE
              }
            />
            <WSDataItem
              label="Pay period"
              value={formatPayPeriodRange(
                payrollRun.payPeriodStartDate,
                payrollRun.payPeriodEndDate
              )}
            />
            <WSDataItem
              label="Pay date"
              value={
                payrollRun.payDate
                  ? toWSDateString(payrollRun.payDate, "monthDayYear")
                  : EMPTY_VALUE
              }
            />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSCard>
  );
};
